import React, { useEffect, useState } from 'react';
import api from '../utils/api';
import { useTenant } from '../context/TenantContext';
import '../styles/UpdateWebhooks.css';
import logger from '../utils/logger';

const UpdateWebhooks = () => {
  const { tenant, loading: tenantLoading } = useTenant();
  const [webhooks, setWebhooks] = useState({
    step1: '',
    step4: '',
    final: '',
    followup_step1: '',
    followup_submit: ''
  });
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(true);
  const [message, setMessage] = useState('');

  // Fetch current webhooks for this tenant
  useEffect(() => {
    const fetchWebhooks = async () => {
      if (tenantLoading || !tenant?.id) {
        logger.log('[UpdateWebhooks] Waiting for tenant to load...');
        return;
      }

      try {
        setFetchLoading(true);
        logger.log('[UpdateWebhooks] Fetching webhooks for tenant:', tenant.id);

        const res = await api.get(`/api/webhooks/tenant/${tenant.id}`);
        logger.log('[UpdateWebhooks] Received webhooks:', res.data.webhooks);

        setWebhooks({
          step1: res.data.webhooks?.step1 || '',
          step4: res.data.webhooks?.step4 || '',
          final: res.data.webhooks?.final || '',
          followup_step1: res.data.webhooks?.followup_step1 || '',
          followup_submit: res.data.webhooks?.followup_submit || ''
        });
      } catch (err) {
        logger.error('[UpdateWebhooks] Error fetching webhooks:', err);
        setMessage('Error fetching webhooks');
      } finally {
        setFetchLoading(false);
      }
    };
    fetchWebhooks();
  }, [tenant?.id, tenantLoading]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setWebhooks({ ...webhooks, [name]: value });
  };

  const handleUpdate = async (step) => {
    if (!tenant?.id) {
      setMessage('Error: No tenant selected');
      return;
    }

    try {
      setLoading(true);
      setMessage('');

      logger.log('[UpdateWebhooks] Updating webhook for tenant:', tenant.id, 'step:', step, 'url:', webhooks[step]);

      const res = await api.post(`/api/webhooks/tenant/${tenant.id}/update`, {
        step,
        url: webhooks[step],
      });

      logger.log('[UpdateWebhooks] Update response:', res.data);
      setMessage(res.data.message);
    } catch (err) {
      logger.error('[UpdateWebhooks] Error updating webhook:', err);
      setMessage('Error updating webhook');
    } finally {
      setLoading(false);
    }
  };

  // Define all webhook steps - Intake Form and Follow-Up Form
  const intakeSteps = [
    { key: 'step1', label: 'Intake - Patient Onboarding (Step 1)', description: 'Triggered when patient submits initial info' },
    { key: 'step4', label: 'Intake - Family History (Step 4)', description: 'Triggered after family history step' },
    { key: 'final', label: 'Intake - Final Submit', description: 'Triggered when full intake form is submitted' }
  ];

  const followupSteps = [
    { key: 'followup_step1', label: 'Follow-Up - First Step', description: 'Triggered when existing patient starts follow-up' },
    { key: 'followup_submit', label: 'Follow-Up - Final Submit', description: 'Triggered when follow-up form is submitted' }
  ];

  if (tenantLoading || fetchLoading) {
    return (
      <div className="update-webhooks-container">
        <h2>Update Webhooks</h2>
        <p>Loading webhooks...</p>
      </div>
    );
  }

  return (
    <div className="update-webhooks-container">
      <h2>Update Webhooks</h2>
      <p style={{ color: '#666', marginBottom: '20px', fontSize: '14px' }}>
        Configure webhooks for <strong>{tenant?.brandName || tenant?.name || 'this tenant'}</strong> (ID: {tenant?.id})
      </p>

      {/* Intake Form Webhooks */}
      <div className="webhook-group">
        <h3 style={{ borderBottom: '2px solid var(--color-primary, #06AB8B)', paddingBottom: '8px', marginBottom: '15px' }}>
          📋 Intake Form Webhooks
        </h3>
        {intakeSteps.map(({ key, label, description }) => (
          <div className="webhook-section" key={key}>
            <label>{label}</label>
            <small style={{ display: 'block', color: '#888', marginBottom: '8px' }}>{description}</small>
            <input
              type="text"
              name={key}
              value={webhooks[key] || ''}
              onChange={handleChange}
              placeholder={`Enter ${label} webhook URL`}
            />
            <button onClick={() => handleUpdate(key)} disabled={loading}>
              {loading ? 'Updating...' : 'Update'}
            </button>
          </div>
        ))}
      </div>

      {/* Follow-Up Form Webhooks */}
      <div className="webhook-group" style={{ marginTop: '30px' }}>
        <h3 style={{ borderBottom: '2px solid #f59e0b', paddingBottom: '8px', marginBottom: '15px' }}>
          🔄 Follow-Up Form Webhooks
        </h3>
        {followupSteps.map(({ key, label, description }) => (
          <div className="webhook-section" key={key}>
            <label>{label}</label>
            <small style={{ display: 'block', color: '#888', marginBottom: '8px' }}>{description}</small>
            <input
              type="text"
              name={key}
              value={webhooks[key] || ''}
              onChange={handleChange}
              placeholder={`Enter ${label} webhook URL`}
            />
            <button onClick={() => handleUpdate(key)} disabled={loading}>
              {loading ? 'Updating...' : 'Update'}
            </button>
          </div>
        ))}
      </div>

      {message && (
        <div className={`message ${message.includes('Error') ? 'error' : 'success'}`}>
          {message}
        </div>
      )}

      {/* Debug Info */}
      <div style={{ marginTop: '30px', padding: '15px', background: '#f5f5f5', borderRadius: '8px', fontSize: '12px' }}>
        <strong>Debug Info:</strong>
        <pre style={{ margin: '10px 0 0', whiteSpace: 'pre-wrap' }}>
          {JSON.stringify({ tenantId: tenant?.id, webhooks }, null, 2)}
        </pre>
      </div>
    </div>
  );
};

export default UpdateWebhooks;
